<template>
  <Card cardClasses="shadow border-0 rounded-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h3 class="mb-0 text-primary">
          <vue-feather type="user" size="20" class="me-2"></vue-feather>
          My Profile
        </h3>
        <div>
          <button class="btn btn-outline-dark me-2 rounded-pill" @click="$router.go(-1)">
            <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
            Back
          </button>
          <button class="btn btn-outline-primary rounded-pill" @click="showModal = true">
            <vue-feather type="edit" size="16" class="me-1"></vue-feather>
            Edit Profile
          </button>
        </div>
      </div>

    <!-- Profile Header Section -->
    <div class="profile-banner rounded-4 p-4 bg-gradient shadow mb-4">
      <div class="row align-items-center">
        <div v-if="loading" class="text-center py-4 col-12">
          <Spinner spinner-class="mb-1" />
        </div>
        <div v-if="!loading" class="col-md-12">
          <div class="row align-items-center">
            <div class="col-md-3 col-lg-2 text-center mb-3 mb-md-0">
              <div class="position-relative d-inline-block">
                <img class="rounded-circle img-thumbnail shadow-sm" style="width: 150px; height: 150px; object-fit: cover;" :src="avatarUrl + user.avatar" alt="">
                <button class="btn btn-sm btn-primary position-absolute bottom-0 end-0 rounded-5 p-2" @click="$refs.refInputEl.click()" title="Upload Photo">
                  <vue-feather type="camera" size="16"></vue-feather>
                </button>
                <input
                    ref="refInputEl"
                    type="file"
                    name="logo"
                    accept=".jpeg,.png,.jpg"
                    hidden
                    @input="changeAvatar"
                >
              </div>
            </div>
            <div class="col-md-9 col-lg-10">
              <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
                <h2 class="mb-0 fw-bold text-primary">{{ user.first_name }} {{ user.last_name }}</h2>
                <badge class="ms-2 badge-lg" type="success">
                  <vue-feather type="check-circle" size="14" class="me-1"></vue-feather>
                  {{ user.role }}
                </badge>
              </div>
              <div class="row g-3">
                <div class="col-md-4 col-lg-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="phone" size="16" class="text-secondary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Phone</div>
                      <div class="fw-medium">{{ user.phone }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 col-lg-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="mail" size="16" class="text-secondary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Email</div>
                      <div class="fw-medium">{{ user.email }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 col-lg-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="calendar" size="16" class="text-secondary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Joined</div>
                      <div class="fw-medium">{{ formatDate(user.created_at) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Password Change Section -->
    <Card cardClasses="shadow border-0 rounded-4 mb-4">
      <div class="d-flex align-items-center mb-4">
        <div class="quick-link-icon quick-link-icon-primary rounded-4 me-3 d-flex align-items-center justify-content-center">
          <vue-feather type="lock" stroke="#ffffff" size="24"></vue-feather>
        </div>
        <h5 class="mb-0 fw-bold">Change Password</h5>
      </div>
      <div class="p-1">
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="col-md-6">
              <UInput v-model="userPass.password" placeholder="Enter new password" type="password">
                <template #label>New Password</template>
              </UInput>
            </div>
            <div class="col-md-6">
              <UInput v-model="userPass.password_confirmation" placeholder="Confirm new password" type="password">
                <template #label>Confirm Password</template>
              </UInput>
            </div>
          </div>
          <div class="mt-4 d-flex justify-content-end">
            <Button class="btn-primary rounded-pill" @click="updatePassword">
              <vue-feather type="save" size="16" class="me-1"></vue-feather>
              Update Password
            </Button>
          </div>
        </form>
      </div>
    </Card>

    <!-- Edit Profile Modal -->
    <Modal :open="showModal" @closed="showModal = false" dialog-size="modal-lg">
      <div class="modal-header">
        <div class="d-flex align-items-center">
          <div class="quick-link-icon quick-link-icon-primary rounded-4 me-3 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
            <vue-feather type="edit-3" stroke="#ffffff" size="18"></vue-feather>
          </div>
          <h5 class="modal-title m-0 fw-bold">Edit Profile</h5>
        </div>
        <button @click="showModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="form-group col-md-6">
              <label for="first_name" class="form-label fw-bold">First Name</label>
              <input type="text" class="form-control form-control-lg rounded-3" v-model="user.first_name">
            </div>
            <div class="form-group col-md-6">
              <label for="last_name" class="form-label fw-bold">Last Name</label>
              <input type="text" class="form-control form-control-lg rounded-3" v-model="user.last_name">
            </div>
            <div class="form-group col-md-6">
              <label for="phone" class="form-label fw-bold">Phone</label>
              <input type="text" class="form-control form-control-lg rounded-3" v-model="user.phone">
            </div>
            <div class="form-group col-md-6">
              <label for="email" class="form-label fw-bold">Email</label>
              <input type="text" class="form-control form-control-lg rounded-3" v-model="user.email">
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-dark rounded-pill me-3" data-dismiss="modal" @click="showModal = false">
          <vue-feather type="x" size="16" class="me-1"></vue-feather>
          Cancel
        </button>
        <button type="button" class="btn btn-primary rounded-pill" @click="submitForm">
          <vue-feather type="save" size="16" class="me-1"></vue-feather>
          Save Changes
        </button>
      </div>
    </Modal>
    <!-- Edit Profile Modal End -->

  </Card>
</template>

<style scoped>
/* Profile Banner */
.profile-banner {
  background: #652aa3; /* Fallback color */
  background: linear-gradient(135deg, #652aa3 0%, #a32a4f 100%);
  position: relative;
  overflow: hidden;
}

.profile-banner::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  bottom: -50%;
  left: -50%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
  transform: rotate(25deg);
  pointer-events: none;
}

/* Card styles */
.card {
  transition: all 0.3s ease;
  border-radius: 1rem;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.1) !important;
}

/* Quick link icons */
.quick-link-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quick-link-icon-primary {
  background-color: #652aa3;
}

.quick-link-icon-secondary {
  background-color: #a32a4f;
}

.quick-link-icon-success {
  background-color: #2aa367;
}

.text-primary {
  color: #652aa3 !important;
}
</style>

<script>
import userService from "@/api/userService";
import formatDate from "../../utils/formatDate";
import loadingButton from "@/utils/loadingButton";
import VueFeather from 'vue-feather';

export default {
  name: "UserProfile",
  components: { VueFeather },
  data() {
    return {
      loading: true,
      showModal: false,
      user: {},
      userPass: {
        password: '',
        password_confirmation: ''
      },
      avatarUrl: process.env.VUE_APP_API_IMAGE_URL + 'user/',
    }
  },
  methods: {
    formatDate,
    submitForm: async function (e) {
      loadingButton(e)
      let response = await userService.updateProfile({
        first_name: this.user.first_name,
        last_name: this.user.last_name,
        phone: this.user.phone,
        email: this.user.email
      })
      if (response)
        this.showModal = false
      loadingButton(e, false)
    },
    async updatePassword(e) {
      loadingButton(e)
      await userService.changePassword(this.userPass, this.user.id)
      loadingButton(e, false)
    },
    changeAvatar : async function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        fileReader.onload = async () => {
          if (typeof fileReader.result === 'string')
            await userService.updateAvatar({ file: fileReader.result })
        }
      }
    }
  },
  created: async function () {
    this.user = await userService.getProfile()
    this.loading = false
  }
}
</script>
